import type { ReactNode } from 'react'

/**
 * Shared pieces of the block cards (#205 blocks mockup): a labelled section,
 * the severity + score unit, and an inline key/value row. Layout classes are
 * `wb-sec*`, `wb-sev`, `wb-inline-kv` in app.css.
 */

/** One labelled section of a block card. `count` renders muted after the label. */
export function Section({ label, count, children }: { label: string; count?: number; children: ReactNode }) {
  return (
    <section className="wb-sec" aria-label={label}>
      <h4 className="wb-sec-label">
        {label}
        {count !== undefined ? <span className="n">{count}</span> : null}
      </h4>
      {children}
    </section>
  )
}

/** Severity (and score) in the severity colour: "● HIGH · 9". */
export function SeverityUnit({ severity, score }: { severity: string; score?: string }) {
  if (!severity) return <span className="wb-sev">—</span>
  return (
    <span className="wb-sev" data-sev={severity.toLowerCase()}>
      <span className="dot" aria-hidden="true" />
      {severity}
      {score ? <span className="sc">· {score}</span> : null}
    </span>
  )
}

/**
 * A visible field label above a control that carries only an accessible name
 * (PickList), so it lines up with labelled TextFields in the same row. The
 * visible text is aria-hidden: the control's own label is what is announced.
 */
export function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bui-field">
      <span className="bui-label" aria-hidden="true">{label}</span>
      {children}
    </div>
  )
}

/**
 * Keys over values, laid out in a wrapping row. Empty values render "—"; a
 * value may be any node (the Signal lead carries the severity unit).
 */
export function InlineKv({ items, className }: { items: Array<[string, ReactNode]>; className?: string }) {
  return (
    <dl className={className ? `wb-inline-kv ${className}` : 'wb-inline-kv'}>
      {items.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v || '—'}</dd>
        </div>
      ))}
    </dl>
  )
}
